import _ from 'lodash';
import moment from 'moment';

import captureException from 'services/captureException';
import getFieldType from 'utils/getFieldType';
import metricTypeCheckers from 'types/metricTypeCheckers';
import formatDateLabel from 'components/V5Gadget/formatDateLabel';
import { toInterval } from 'components/V5Gadget/toAutoInterval';
import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

import build2DDateComparisonRowData from './build2DDateComparisonRowData';
import buildRowData from './buildRowData';

const buildComparisonRowData = ({
  current,
  previous,
  usedMetrics,
  chartDef,
  groupByField,
  unitsLocale,
  comparison,
  baseViews,
  datasets,
}: {
  current: MatrixRowData[];
  previous: MatrixRowData[];
  usedMetrics: Metrics.Metric[];
  chartDef: V5ChartDefinition;
  groupByField: string;
  unitsLocale: string;
  comparison?: PersistedComparisonType;
  baseViews: FleetOps.BaseView[];
  datasets: FleetOps.DatasetDefinition[];
}): MatrixComparisonRowData[] => {
  const isTimeTrend =
    chartDef.trendByCalendarInterval &&
    ['day', 'week', 'month', 'quarter', 'auto'].includes(
      chartDef.trendByCalendarInterval,
    );
  const isPreviousAcceptable =
    chartDef.series.some((s) => s.matrixCellType !== undefined) && !!comparison;
  // A secondary row dimension makes a row per (A, secondary A) pair, so a
  // row is found by both
  const secondaryField =
    groupByField !== 'date' &&
    !chartDef.dimensionB &&
    isSecondaryDimensionAInEffect(chartDef)
      ? chartDef.secondaryDimensionA?.field
      : undefined;
  const isSameRow = ({
    row,
    group,
    secondary,
  }: {
    row: MatrixRowData;
    group: unknown;
    secondary: unknown;
  }) =>
    row[groupByField] === group &&
    (!secondaryField || row[secondaryField] === secondary);
  const groupings: { group: unknown; secondary?: unknown }[] =
    groupByField === 'date'
      ? current
          .map((r) => ({ group: r[groupByField] }))
          .filter(({ group }) => typeof group === 'string')
      : _.uniqBy(
          [...current, ...(isPreviousAcceptable ? previous : [])].map((r) => ({
            group: r[groupByField],
            secondary: secondaryField ? r[secondaryField] : undefined,
          })),
          ({ group, secondary }) => JSON.stringify([group, secondary ?? null]),
        ).filter(({ group }) => typeof group === 'string');

  return groupings.map(({ group, secondary }, index) => {
    const currentRow = current.find((row) =>
      isSameRow({ row, group, secondary }),
    );
    const previousRow = (() => {
      if (chartDef.trendByCalendarInterval) {
        if (!comparison) {
          return undefined;
        } else if (comparison.compareType === 'previous') {
          if (index === 0) {
            return previous[previous.length - 1];
          } else {
            return current[index - 1];
          }
        } else {
          return previous[index];
        }
      } else {
        return previous.find((row) => isSameRow({ row, group, secondary }));
      }
    })();
    const comparisonRow = {} as MatrixComparisonRowData;
    if (isTimeTrend && chartDef.trendByCalendarInterval && currentRow) {
      comparisonRow['date'] = formatDateLabel(
        currentRow['raw-date'] as string,
        chartDef.trendByCalendarInterval,
        false,
        undefined,
        chartDef.gadgetType === 'matrix',
        false,
        chartDef.gadgetType === 'matrix',
      );
      comparisonRow['raw-date'] = currentRow['raw-date']
        ? (currentRow['raw-date'] as string)
        : (currentRow[groupByField] as string);
    } else {
      comparisonRow[groupByField] = group as string;
    }
    if (secondaryField) {
      comparisonRow[secondaryField] = secondary as string;
    }

    const fieldsToMap = currentRow
      ? Object.keys(currentRow)
      : previousRow
        ? Object.keys(previousRow)
        : [];

    fieldsToMap.forEach((key) => {
      const m = usedMetrics.find((mo) => mo.id === key);
      if (m) {
        const s = chartDef.series.find((s) => s.metricId === m.id);
        if (s) {
          const currentDateRange = (() => {
            const indexToUse = (() => {
              if (current.length > index) {
                return index;
              } else if (!isTimeTrend && current.length > 0) {
                return 0;
              } else {
                return index;
              }
            })();
            if (current.length > indexToUse) {
              const rawDate = current[indexToUse]['raw-date'] as string;
              if (current[indexToUse].interval) {
                const interval = toInterval(
                  current[indexToUse].interval as string,
                );
                return {
                  startDate: rawDate,
                  endDate: moment(rawDate)
                    .add({ [interval || 'month']: 1 })
                    .subtract({ day: 1 })
                    .format('YYYY-MM-DD'),
                };
              } else {
                return current[indexToUse].dateScope as DateRangeInput;
              }
            }
          })();

          const previousDateRange = (() => {
            try {
              if (previousRow) {
                if (previousRow['raw-date'] && previousRow['interval']) {
                  const interval = toInterval(
                    previousRow['interval'] as string,
                  );
                  const rawDate = previousRow['raw-date'] as string;
                  return {
                    startDate: rawDate,
                    endDate: moment(rawDate)
                      .add({ [interval || 'month']: 1 })
                      .subtract({ day: 1 })
                      .format('YYYY-MM-DD'),
                  };
                }
                return previousRow['dateScope'] as DateRangeInput;
              }

              if (previous[0] && previous[0]['dateScope']) {
                return previous[0]['dateScope'] as DateRangeInput;
              }

              if (
                previous[0] &&
                previous[0]['raw-date'] &&
                previous[0]['interval']
              ) {
                const interval = toInterval(previous[0]['interval'] as string);
                const rawDate = previous[0]['raw-date'] as string;
                return {
                  startDate: rawDate,
                  endDate: moment(rawDate)
                    .add({ [interval || 'month']: 1 })
                    .subtract({ day: 1 })
                    .format('YYYY-MM-DD'),
                };
              }
              return undefined;
            } catch (ex) {
              ex.name = 'Comparison failed';
              captureException(ex);
              return undefined;
            }
          })();

          const isTextFieldType = (() => {
            if (!metricTypeCheckers.isNormalMetric(m)) {
              return undefined;
            }

            const fieldType = getFieldType({
              datasets,
              datasetType: m.dataType,
              fieldName: m.field,
            });

            return fieldType === 'text';
          })();

          const isPreviousAcceptable =
            metricTypeCheckers.isNormalMetric(m) &&
            ['first', 'last'].includes(m.aggFunc) &&
            isTextFieldType &&
            !!comparison;
          if (s.matrixCellType) {
            const prev = previousRow
              ? (previousRow[`${m.id}-raw`] as string | number)
              : (0 as string | number);

            const deltaCol = {
              value: currentRow
                ? (currentRow[`${m.id}-raw`] as string | number)
                : isPreviousAcceptable
                  ? prev
                  : (0 as string | number),
              previous: prev,
              formatting: m.formatting,
              unitsLocale,
              type: 'delta' as MatrixCellType,
              currentDateRange: currentDateRange,
              previousDateRange: previousDateRange,
              metric: m,
            };
            const percentDeltaCol = {
              value: currentRow
                ? (currentRow[`${m.id}-raw`] as string | number)
                : (0 as string | number),
              previous: previousRow
                ? (previousRow[`${m.id}-raw`] as string | number)
                : (0 as string | number),
              formatting: m.formatting,
              unitsLocale,
              type: 'percentDelta' as MatrixCellType,
              currentDateRange: currentDateRange,
              previousDateRange: previousDateRange,
              metric: m,
            };

            if (s.matrixCellType === 'both') {
              comparisonRow[`${m.id}-delta`] = deltaCol;
              comparisonRow[`${m.id}-percentDelta`] = percentDeltaCol;
            } else if (s.matrixCellType === 'delta') {
              comparisonRow[`${m.id}-delta`] = deltaCol;
            } else {
              comparisonRow[`${m.id}-percentDelta`] = percentDeltaCol;
            }

            // @ts-ignore
            comparisonRow['filterInput'] = currentRow
              ? (currentRow['filterInput'] as FilterInput | undefined)
              : undefined;
            comparisonRow[m.id] = currentRow
              ? (currentRow[m.id] as string)
              : '-';
          } else {
            // @ts-ignore
            comparisonRow['filterInput'] = currentRow
              ? (currentRow['filterInput'] as FilterInput | undefined)
              : undefined;
            const v =
              !!currentRow && currentRow[m.id] !== undefined
                ? (currentRow[m.id] as string)
                : isPreviousAcceptable
                  ? !!previousRow && previousRow[m.id] !== undefined
                    ? (previousRow[m.id] as string)
                    : '-'
                  : '-';
            comparisonRow[m.id] = v;
          }

          // Threshold colours read the unformatted value
          if (s.cellColourConfig && currentRow) {
            // @ts-ignore - raw values are numbers, not formatted strings
            comparisonRow[`${m.id}-raw`] = currentRow[`${m.id}-raw`];
          }

          if (s.matrixShowComparisonValue) {
            // previousRow[m.id] is already the formatted comparison-period
            // value (buildRowData formats [m.id] and keeps [m.id]-raw). Storing
            // the formatted string lets the plain-string cell renderer display
            // it and cellComparator sort it numerically, matching the current
            // value column.
            comparisonRow[`${m.id}-comparisonValue`] =
              previousRow && previousRow[m.id] !== undefined
                ? (previousRow[m.id] as string)
                : '-';
          }
        }
      }
    });

    return comparisonRow;
  });
};

export { buildRowData, build2DDateComparisonRowData };
export default buildComparisonRowData;
